// Custom hooks logic based on https://tkdodo.eu/blog/working-with-zustand

import { create } from 'zustand'
import { devtools, persist, createJSONStorage } from 'zustand/middleware'

// The dates below represent a date-only string like `2026-03-27` in `Europe/Oslo` timezone.
type State = {
  pickupDateBag?: string
  pickupDateContainer?: string
  deliveryDateContainer?: string
}

type Actions = {
  setPickupDateBag: (date: string) => void
  setPickupDateContainer: (date: string) => void
  setDeliveryDateContainer: (date: string) => void
  reset: () => void
}

type DateStore = State & Actions

const initialState: State = {
  pickupDateBag: undefined,
  pickupDateContainer: undefined,
  deliveryDateContainer: undefined,
}

// ⬇️ not exported, so that no one can subscribe to the entire store
const useDateStore = create<DateStore>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        setPickupDateBag: (date) => set({ pickupDateBag: date }),
        setPickupDateContainer: (date) => set({ pickupDateContainer: date }),
        setDeliveryDateContainer: (date) => set({ deliveryDateContainer: date }),
        reset: () => set(initialState),
      }),
      {
        name: 'date-store',
        storage: createJSONStorage(() => sessionStorage),
      },
    ),
  ),
)

// 💡 exported - consumers don't need to write selectors
export const usePickupDateBag = () => useDateStore((state: DateStore) => state.pickupDateBag)
export const usePickupDateContainer = () => useDateStore((state: DateStore) => state.pickupDateContainer)
export const useDeliveryDateContainer = () => useDateStore((state: DateStore) => state.deliveryDateContainer)

// 🎉 one selector for all our actions
export const useDateStoreActions = () =>
  useDateStore((state: DateStore) => ({
    setPickupDateBag: state.setPickupDateBag,
    setPickupDateContainer: state.setPickupDateContainer,
    setDeliveryDateContainer: state.setDeliveryDateContainer,
    reset: state.reset,
  }))
